import elaReadingSkillsMapping from "@/view/TeacherActivityContainer/utils/elaReadingSkillsMapping";
import splitReadingSkillName from "@/view/TeacherActivityContainer/utils/splitReadingSkillName";

const READING_SKILLS = "READING_SKILLS";

/**
 * Parses metadata response and returns a shape used to easily determine
 * which questions pertain to a reading skill.
 *
 * @param {Object} data
 * @param {array} ids
 * @returns {array} cleanData
 */
export const cleanUpMetadata = (data, ids) => {
  if (!data) return null;

  const cleanData = data.reduce((acc, value, index) => {
    const readingSkill = value?.data?.find(
      (item) => item.content_type === READING_SKILLS,
    );
    const splitReadingSkillTitle = splitReadingSkillName(
      readingSkill?.data?.name,
    );
    const readingSkillTitle = elaReadingSkillsMapping[splitReadingSkillTitle]
      ? elaReadingSkillsMapping[splitReadingSkillTitle]
      : splitReadingSkillTitle;

    const quizQuestionReadingSkill = { [readingSkillTitle]: ids[index] };

    acc.push(quizQuestionReadingSkill);
    return acc;
  }, []);

  return cleanData;
};

/**
 * Given the currently selected reading skills along with all other levels mapped to their reading skill,
 * find a match for each currently selected level.
 *
 * @param {Object} currentSelectionState
 * @param {array} leveledQuizQuestionIds
 * @returns {array} allOtherQuestionIdsMatched
 */
export const getMatchingReadingSkillQuestionIds = (
  currentSelectionState,
  leveledQuizQuestionIds,
) => {
  const allOtherQuestionIdsMatched = [];
  let skillsTrackerArray = leveledQuizQuestionIds;

  for (const [readingSkillTitle, readingSkillSelectionState] of Object.entries(
    currentSelectionState,
  )) {
    // Ignore deselected reading skills and move to next iteration.
    if (readingSkillSelectionState.isReadingSkillSelected === false) {
      continue;
    }
    for (const nestedValue of Object.values(readingSkillSelectionState)) {
      if (nestedValue.isQuizQuestionSelected) {
        // Find a first matching question in skillsTrackerArray.
        const firstMatchId = skillsTrackerArray.find(
          (obj) => obj[readingSkillTitle],
        );
        if (firstMatchId) {
          allOtherQuestionIdsMatched.push(firstMatchId[readingSkillTitle]);
          // Remove the item from our tracker so we don't include the same id in a future iteration.
          skillsTrackerArray = skillsTrackerArray.filter(
            (obj) => obj[readingSkillTitle] !== firstMatchId[readingSkillTitle],
          );
        }
      }
    }
  }

  return allOtherQuestionIdsMatched;
};

/**
 * Handles getting metadata for each question and mapping question id
 * to reading skill title.
 *
 * @param {Object} elaQuizData
 * @param {Function} getQuizQuestionMetadata
 * @returns {Object} result
 */
export const getMappedQuestionIdsToReadingSkill = async ({
  quizData,
  getQuizQuestionMetadata,
}) => {
  const mappedQuestions = await quizData?.data?.levels.reduce(
    async (acc, level) => {
      const resolvedAcc = await acc;
      const idsToInclude = level?.questions.map((question) => question?.id);
      const questionIdsMetadata = await getQuizQuestionMetadata(idsToInclude);
      const parsedData = cleanUpMetadata(questionIdsMetadata, idsToInclude);

      resolvedAcc[level?.id] = parsedData;
      return resolvedAcc;
    },
    Promise.resolve({}),
  );

  return mappedQuestions;
};

/**
 * Formats custom quiz for assignment. Responsible for fetching metadata across all quiz levels
 * and finding matching questions that belong to the reading skills selected.
 *
 * @param {Array} allElaLanguagedQuizzes
 * @param {Function} getQuizQuestionMetadata
 * @param {Object} readingSkillState
 * @returns {Object} activity_element_selections value
 */
const formatCustomQuizForAssignment = async ({
  allElaLanguagedQuizzes,
  getQuizQuestionMetadata,
  readingSkillState,
}) => {
  let selectedQuizQuestionIds = [];
  const result = {};

  Object.values(readingSkillState).forEach((questionData) => {
    const questionIdsArray = Object.keys(questionData).filter(
      (data) => data !== "isReadingSkillSelected",
    );

    if (questionData.isReadingSkillSelected) {
      const selectedQuestions = questionIdsArray.filter(
        (id) => questionData[id].isQuizQuestionSelected,
      );
      selectedQuizQuestionIds =
        selectedQuizQuestionIds.concat(selectedQuestions);
    }
  });

  allElaLanguagedQuizzes.forEach((quizContainer) => {
    result[quizContainer?.id] = {};
  });

  for (const key of Object.keys(result)) {
    const quizContainerId = key;
    const quizData = allElaLanguagedQuizzes.find(
      (quiz) => quiz.id === quizContainerId,
    );

    // Get metadata for all other ELA quiz questions across all levels.
    const mappedLeveledQuestionIdsToReadingSkill =
      await getMappedQuestionIdsToReadingSkill({
        quizData,
        getQuizQuestionMetadata,
      });

    // Match reading skills based off state selection.
    const matchingSkillsToCurrentSelection = {};
    for (const [key, value] of Object.entries(
      mappedLeveledQuestionIdsToReadingSkill,
    )) {
      const selectedIds = getMatchingReadingSkillQuestionIds(
        readingSkillState,
        value,
      );
      matchingSkillsToCurrentSelection[key] = selectedIds;
    }

    result[quizContainerId] = matchingSkillsToCurrentSelection;
  }

  return result;
};

export default formatCustomQuizForAssignment;
